/** Banner on the staff dashboard asking the signed-in staff member to add their mobile so texts can reach them. */
import React, { useState } from 'react';
import { MessageSquare, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { formatApiError } from '@/utils/apiError';

const UK_MOBILE = /^(\+44|0)7\d{9}$/;

export function MyMobileBanner({ user, onSaved }) {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  if (!user || !['admin', 'manager', 'team'].includes(user.role) || done) return null;
  if (UK_MOBILE.test((user.phone || '').replace(/[\s-]/g, ''))) return null;

  const save = async () => {
    if (!UK_MOBILE.test(val.replace(/[\s-]/g, ''))) { toast.error('Enter a UK mobile like 07xxx xxxxxx'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/users/${user.user_id}/mobile`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone: val }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(d) || 'Could not save');
      setDone(true); toast.success('Mobile saved — you can now receive text alerts'); onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 mt-2" data-testid="my-mobile-banner">
      <div className="flex flex-wrap items-center gap-3 bg-sky-50 border border-sky-200 text-sky-950 rounded-lg px-4 py-3">
        <MessageSquare className="w-5 h-5 text-sky-600 shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-bold text-sm">Add your mobile to get text alerts</div>
          <div className="text-xs text-sky-900/80">Ox Resin sends short texts for things like @mentions and timesheet reminders. Only staff can see it.</div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} placeholder="07xxx xxxxxx" inputMode="tel"
            className="h-9 flex-1 sm:w-44 text-sm border border-sky-300 rounded-md px-3 bg-white" data-testid="my-mobile-input" />
          <Button size="sm" onClick={save} disabled={busy} className="h-9 bg-sky-700 hover:bg-sky-600 text-white font-bold" data-testid="my-mobile-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 sm:mr-1" />}<span className="hidden sm:inline">Save</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
